import type { CSSProperties } from 'react'
import styled from 'styled-components/macro'

import { DateRangePicker, type DateRangePickerProps } from '@loadsmart/loadsmart-ui'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Field } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { toArray } from '../../../utils/array'
import { formatDate, parseISO } from '../../../utils/date'
import { isString } from '../../../utils/string'

const DatePickerWrapper = styled.div`
  /*
   * NOTE: The inputs of the DateRangePicker do not grow to match the container.
   * The DateRangePicker also do not propagate the class and style props and do
   * not export the internal components to make a decent query. And because the
   * lib make use of CSS-in-JS I cannot query by the classes.
   * Finally, changing the component itself could introduce a number of
   * unexpected regressions on other apps that make use of it and have some sort
   * of 'gambiarra' like this one. It's possible to find some on this app.
   * So, while the new component is not implemented on Miranda4All, I think this
   * is the best quick-win for this problem.
   */

  [role='menubar'] {
    div:nth-child(1) {
      flex: 1;
    }

    div:nth-child(2) {
      flex: 1;
    }

    div:only-child {
      gap: ${getToken('spacing-4')};
    }
  }
`

export type DateSelectProps = Omit<
  DateRangePickerProps,
  'value' | 'onChange' | 'getRangeStartInputProps' | 'getRangeEndInputProps'
> & {
  label: string
  style?: CSSProperties
  startDatePlaceholder?: string
  endDatePlaceholder?: string
  startDateValue?: string
  endDateValue?: string
  dateFormat?: string
  onChange: (newValue: { startDate?: string; endDate?: string }) => void
}

export function DateSelect({
  startDatePlaceholder,
  endDatePlaceholder,
  startDateValue,
  endDateValue,
  dateFormat,
  id = 'date-select',
  label,
  style,
  onChange,
  ...props
}: DateSelectProps) {
  function handleOnChange(event: EventLike<[string | null, string | null] | null>) {
    const dateRange = toArray(event.target.value)
    const [newStartDate, newEndDate] = dateRange

    let startDate = newStartDate ?? undefined
    let endDate = newEndDate ?? undefined

    if (dateFormat) {
      startDate = isString(newStartDate)
        ? formatDate(parseISO(newStartDate), dateFormat)
        : undefined
      endDate = isString(newEndDate) ? formatDate(parseISO(newEndDate), dateFormat) : undefined
    }

    onChange({ startDate, endDate })
  }

  return (
    <Field style={{ ...style, width: '100%' }} id="date-select-wrapper">
      {label && <Field.Label id={id}>{label}</Field.Label>}
      <DatePickerWrapper>
        <DateRangePicker
          id={id}
          value={[startDateValue ?? null, endDateValue ?? null]}
          getRangeStartInputProps={() => ({ placeholder: startDatePlaceholder ?? 'Start date' })}
          getRangeEndInputProps={() => ({ placeholder: endDatePlaceholder ?? 'End date' })}
          onChange={handleOnChange}
          {...props}
        />
      </DatePickerWrapper>
    </Field>
  )
}
